import { z } from "zod";

import { createLegalOptionalStatementSchema, LEGAL_FORM_MESSAGES, LegalPersonalDetailsSchema } from "./shared";

/** § 312k BGB — website entry control label (footer, global). */
export const CONTRACT_CANCELLATION_ENTRY_BUTTON_LABEL = "Vertrag kündigen";

/** § 312k BGB — confirmation step on the cancellation page. */
export const CONTRACT_CANCELLATION_CONFIRM_BUTTON_LABEL = "Jetzt kündigen";

/** Label for proceeding from the cancellation form to the review step (not the statutory entry control). */
export const CONTRACT_CANCELLATION_FORM_CONTINUE_LABEL = "Weiter zur Bestätigung";

/** E-mail subject for the confirmation link sent to the Stripe customer address. */
export const CONTRACT_CANCELLATION_CONFIRMATION_EMAIL_SUBJECT = "Bitte bestätige deine Kündigung";

/** E-mail subject for extraordinary cancellation receipt sent to the customer. */
export const CONTRACT_CANCELLATION_EXTRAORDINARY_RECEIPT_EMAIL_SUBJECT = "Eingang deiner außerordentlichen Kündigung";

/** Review-step submit label for extraordinary cancellations (manual processing). */
export const CONTRACT_CANCELLATION_EXTRAORDINARY_SUBMIT_BUTTON_LABEL = "Anfrage absenden";

/** Success hint shown after an extraordinary cancellation request was submitted. */
export const CONTRACT_CANCELLATION_EXTRAORDINARY_SUCCESS_MESSAGE =
  "Wir haben deine Anfrage auf außerordentliche Kündigung erhalten. Du bekommst in Kürze eine E-Mail mit der Eingangsbestätigung. Wir prüfen dein Anliegen und melden uns innerhalb weniger Werktage.";

/** How long a cancellation confirmation link stays valid (72 hours). */
export const CONTRACT_CANCELLATION_CONFIRMATION_EXPIRY_MS = 72 * 60 * 60 * 1000;

export const ContractCancellationTypeSchema = z.enum(["ordinary", "extraordinary"]);

export const ConfirmContractCancellationSchema = z.object({
  requestId: z.string().min(1, "Anfrage-ID fehlt."),
  token: z.string().min(1, "Bestätigungslink ungültig."),
});

export type ConfirmContractCancellationBody = z.infer<typeof ConfirmContractCancellationSchema>;

export const ContractCancellationSubmitSchema = LegalPersonalDetailsSchema.extend({
  cancellationType: ContractCancellationTypeSchema,
  statement: createLegalOptionalStatementSchema(),
}).superRefine((data, context) => {
  if (data.cancellationType === "extraordinary" && !data.statement) {
    context.addIssue({
      code: "custom",
      message: LEGAL_FORM_MESSAGES.cancellationStatement,
      path: ["statement"],
    });
  }
});

export type ContractCancellationFormInput = z.input<typeof ContractCancellationSubmitSchema>;
export type ContractCancellationSubmitBody = z.output<typeof ContractCancellationSubmitSchema>;

export type ContractCancellationType = z.infer<typeof ContractCancellationTypeSchema>;

export const CONTRACT_CANCELLATION_TYPE_LABELS: Record<ContractCancellationType, string> = {
  ordinary: "Ordentliche Kündigung zum Laufzeitende",
  extraordinary: "Außerordentliche Kündigung",
};

export const getContractCancellationTypeLabel = (cancellationType: string): string =>
  CONTRACT_CANCELLATION_TYPE_LABELS[cancellationType as ContractCancellationType] ?? cancellationType;
